/**
 * Simple in-memory cache for Supabase queries
 * Reduces API calls and improves performance
 */

interface CacheEntry<T> {
  data: T
  timestamp: number
  ttl: number
}

class QueryCache {
  private cache: Map<string, CacheEntry<any>> = new Map()
  private defaultTTL = 5 * 60 * 1000 // 5 minutes default

  /**
   * Get cached data if valid
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key)
    if (!entry) return null

    const now = Date.now()
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key)
      return null
    }

    return entry.data as T
  }

  /**
   * Set cache data
   */
  set<T>(key: string, data: T, ttl?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    })
  }

  /**
   * Clear specific cache entry
   */
  invalidate(key: string): void {
    this.cache.delete(key)
  }

  /**
   * Clear all cache entries matching a pattern
   */
  invalidatePattern(pattern: string): void {
    const regex = new RegExp(pattern)
    for (const key of this.cache.keys()) {
      if (regex.test(key)) {
        this.cache.delete(key)
      }
    }
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Get cache size
   */
  size(): number {
    return this.cache.size
  }
}

// Singleton instance
export const queryCache = new QueryCache()

/**
 * Cache key generators
 */
export const cacheKeys = {
  knowledge: (filters?: any) => `knowledge:${JSON.stringify(filters || {})}`,
  knowledgeItem: (id: string) => `knowledge:item:${id}`,
  tags: () => 'tags:all',
  sources: () => 'sources:all',
  importSources: (type?: string) => `import_sources:${type || 'all'}`,
  dashboard: () => 'dashboard:stats',
  search: (query: string) => `search:${query}`
}

/**
 * Cache-aware fetch wrapper
 */
export async function cachedFetch<T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl?: number
): Promise<T> {
  // Check cache first
  const cached = queryCache.get<T>(key)
  if (cached !== null) {
    console.log(`Cache hit: ${key}`)
    return cached
  }

  // Fetch fresh data
  console.log(`Cache miss: ${key}`)
  const data = await fetcher()

  // Store in cache
  queryCache.set(key, data, ttl)

  return data
}